import { Component, HostListener, Inject, OnInit, inject, DOCUMENT } from '@angular/core';
import {
  ActivatedRoute,
  NavigationEnd,
  NavigationSkipped,
  NavigationStart,
  Router,
} from '@angular/router';
import { SwUpdate, VersionReadyEvent } from '@angular/service-worker';
import { LoadingService } from './services/loading.service';
import { environment } from '../environments/environment';
import { Subscription, filter, fromEvent, interval } from 'rxjs';
import { UserIdleService } from 'angular-user-idle';
import { NzModalService } from 'ng-zorro-antd/modal';
import { TimePopupComponentComponent } from './shared/components/time-popup-component/time-popup-component.component';

import { AuthService } from './core/authentication/auth.service';
import { TabSessionService } from './core/authentication/tab-session.service';
import { TokenService } from '@processo/services/token.service';
import { clearAuthStorage } from './core/helpers/storage.util';
import { logAuthEvent, describeHttpError } from './core/helpers/auth-diag.util';
import { AppstateService } from './shared/state/appstate.service';
import { OverlaySidebarService } from './shared/components/entity-layout/smart-table/overlay-sidebar.service';
import { layoutCache } from './shared/components/entity-layout/smart-table/cache';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  standalone: false
})
export class AppComponent implements OnInit {
  @HostListener('window:resize', ['$event'])
  onResize(event: Event) {
    this.checkScreenSize();
  }
  title = 'processo';
  environmentType!: string;
  ShowLoader: boolean = true;
  router = inject(Router);
  private _loadingService = inject(LoadingService);
  private auth_Service = inject(AuthService);
  private tokenService = inject(TokenService);
  private tabSession = inject(TabSessionService);
  private app_state = inject(AppstateService);
  dialogRef: any;
  isIdleTimerNotRequired = false;
  subscription!: Subscription;
  idleTimerStartSubscription!: Subscription;
  idleTimerStopSubscription!: Subscription;
  pingSubscription!: Subscription;
  private swUpdateSubscription?: Subscription;
  private displacedSubscription?: Subscription;

  constructor(
    private userIdle: UserIdleService,
    private nzModalService: NzModalService,
    private swUpdate: SwUpdate,
    @Inject(DOCUMENT) private document: Document
  ) {
    this.checkScreenSize();
    this.setPageTitleAndIcon();
    this.subscribeToRouterEvents();
  }

  ngOnInit(): void {
    this.initializeIdleTimerSubscriptions();
    //  Clear the cache after using it so it doesn't re-apply on a manual refresh
    this.app_state.clearImportFilter()
    this.initializeSwUpdate();
    this.listenForDisplacement();
  }

  // Only one tab of the origin keeps the session. When a newer tab claims it,
  // this one hands over rather than competing for refresh-token rotations.
  private listenForDisplacement(): void {
    this.displacedSubscription = this.tabSession.displaced$.subscribe(() => {
      this.standDown();
    });
  }

  // Leaves via a full page load rather than a router navigation, because this
  // tab must stop being a running instance of the app. Every in-app exit is
  // unsafe here: authGuard calls clearTokens() when it denies, and a protected
  // route reached with no access token gets a "Token is expired" response that
  // ErrorInterceptor answers with clearTokens() too. Both wipe the shared
  // localStorage that now belongs to the tab taking over.
  private standDown(): void {
    logAuthEvent('tab-displaced');
    this.stopIdleTimer();
    // Closes the window between displacement and the page unloading.
    this.tokenService.clearInMemoryTokens();
    window.location.replace('/session-moved');
  }

  private initializeSwUpdate(): void {
    if (!this.swUpdate.isEnabled) return;

    let applyingUpdate = false;

    const applyPendingUpdate = async (): Promise<void> => {
      if (applyingUpdate) return;
      applyingUpdate = true;
      try {
        const activated = await this.swUpdate.activateUpdate();
        if (activated) {
          document.location.reload();
        } else {
          applyingUpdate = false;
        }
      } catch {
        applyingUpdate = false;
      }
    };

    const checkForUpdate = async (): Promise<void> => {
      try {
        if ('serviceWorker' in navigator) {
          await navigator.serviceWorker.ready;
        }
        const hasUpdate = await this.swUpdate.checkForUpdate();
        if (hasUpdate) {
          await applyPendingUpdate();
        }
      } catch {
        /* ignore offline / CORS errors */
      }
    };

    const versionReadySub = this.swUpdate.versionUpdates
      .pipe(
        filter((evt): evt is VersionReadyEvent => evt.type === 'VERSION_READY'),
      )
      .subscribe(() => {
        applyPendingUpdate();
      });

    const visibilitySub = fromEvent(document, 'visibilitychange').subscribe(
      () => {
        if (document.visibilityState === 'visible') {
          checkForUpdate();
        }
      },
    );

    // Poll while the tab stays open so a deploy triggers reload without manual F5.
    const pollSub = interval(5 * 60 * 1000).subscribe(() => {
      checkForUpdate();
    });

    this.swUpdateSubscription = new Subscription();
    this.swUpdateSubscription.add(versionReadySub);
    this.swUpdateSubscription.add(visibilitySub);
    this.swUpdateSubscription.add(pollSub);

    checkForUpdate();
  }

  checkScreenSize() {
    const width = window.innerWidth;
    if(width <= 800){
      this.app_state.is_mobile.set(true)
    }else{
      this.app_state.is_mobile.set(false)
      this.app_state.show_more_entity_table_action.set(true)
    }
    // Suppress ng-zorro tooltips in narrow layouts (CSS in commom.scss); JS toggle avoids :has() / MQ edge cases
    this.document.body?.classList.toggle('app-mobile-tooltips-off', width <= 1024);
  }
  private setPageTitleAndIcon(): void {
    const link: HTMLLinkElement = this.document.createElement('link');
    link.rel = 'icon';
    link.type = 'image/x-icon';
    this.environmentType = environment.type;
    if (environment.is_artfine) {
      document.title = 'ArtfineBilz';
      link.href = 'assets/images/logo/ArtfineBilzSideLogo.svg';
    } else {
      document.title = 'processo';
      link.href = 'assets/images/logo/favicon.ico';
    }
    const head =
      this.document.head || this.document.getElementsByTagName('head')[0];
    head.appendChild(link);
  }

  private subscribeToRouterEvents(): void {
    this.router.events.subscribe((event) => {
      if (event instanceof NavigationStart) {
        this._loadingService.show();
      } else if (
        event instanceof NavigationEnd ||
        event instanceof NavigationSkipped
      ) {
        this._loadingService.hide();
      }

      if (event instanceof NavigationEnd) {
        this.handleNavigationEnd(event.url);
      }
    });
  }
private _route =inject(ActivatedRoute);
  private handleNavigationEnd(url: string): void {
    const form_id = this._route.snapshot.queryParamMap.get('form_id');
    const correctedUrl = this.router.url;
    this.isIdleTimerNotRequired =
      correctedUrl === '/login' ||
      correctedUrl === '/contact-us' ||
      correctedUrl === '/delete-account' ||
      correctedUrl === '/register' ||
      correctedUrl === '/registernew' ||
      correctedUrl === '/session-moved' ||
      correctedUrl === '/forgot-password';

    if (this.isIdleTimerNotRequired || form_id) {
      this.stopIdleTimer();
    } else {
      this.startIdleTimer();
    }
  }

  private startIdleTimer(): void {
    this.clearSubscription();
    const down$ = fromEvent(document, 'mousedown');
    this.subscription = down$.subscribe(() => {
      this.restartIdleTimer();
    });
    if (this.pingSubscription && !this.pingSubscription.closed) {
      return;
    }
    this.restartIdleTimer();
    this.startWatching();
  }

  private stopIdleTimer(): void {
    this.stopWatching();
    this.clearSubscription();
  }

  private initializeIdleTimerSubscriptions(): void {
    this.idleTimerStartSubscription = this.userIdle
      .onTimerStart()
      .subscribe((count: any) => {
        this.showTimerPopup(count);
      });

    this.idleTimerStopSubscription = this.userIdle.onTimeout().subscribe(() => {
      this.stopIdleTimer();
      this.showAlertPopup();
    });
  }

  private showTimerPopup(count: any): void {
    if (
      count != null &&
      (this.dialogRef === undefined || this.dialogRef === null)
    ) {
      this.dialogRef = this.nzModalService.create({
        nzContent: TimePopupComponentComponent,
        nzWidth: '394px',
        nzFooter: null,
        nzMaskClosable: false,
        nzClosable: false,
        nzWrapClassName: 'modalContainer',
        nzData: {
          bodyText: 'Your session is about to expire',
        },
      });

      if (this.dialogRef.componentInstance) {
        const configValue: any = this.userIdle.getConfigValue();
        this.dialogRef.componentInstance.count = configValue.timeout - count;
        this.dialogRef.afterClose.subscribe((result: any) => {
          if (result && result.clickedCancel) {
            this.dialogRef = null;
          }
          if (result === 'reset') {
            this.dialogRef = null;
            this.subscribeToRouterEvents();
            this.initializeIdleTimerSubscriptions();
          }
        });
      }
    }
  }
  private overlayService = inject(OverlaySidebarService)
  private showAlertPopup(): void {
    this.dialogRef?.close();
    this.dialogRef?.afterClose.subscribe(() => {
      this.dialogRef = undefined;
    });
    logAuthEvent('idle-timeout-logout');
    clearAuthStorage();
    const current = this.router.url;
    if (current !== '/contact-us' && current !== '/delete-account') {
      this.router.navigateByUrl('/login');
    }
    this.app_state.resetAppState()
    this.overlayService.overlayRefs.forEach((e) => {
      if (e) {
        e?.dispose()
      }
    })
  }

  private startWatching(): void {
    this.userIdle.startWatching();
    if (this.pingSubscription) {
      this.pingSubscription.unsubscribe();
    }
    this.pingSubscription= this.userIdle?.ping$?.subscribe(() => {
      const refresh_token: any = localStorage.getItem('refresh_token');
      if (
        refresh_token &&
        refresh_token != '' &&
        refresh_token != 'undefined'
      ) {
        this.auth_Service.updateToken().subscribe({
          error: (err) => {
            // Failed 40-min refresh: nothing tears the session down here,
            // but the NEXT cold start will fail — this is the breadcrumb.
            logAuthEvent('ping-refresh-failed', describeHttpError(err));
          },
        });
      } else {
        logAuthEvent('ping-no-token-logout');
        clearAuthStorage();
        this.app_state.resetAppState()
        const current = this.router.url;
        if (current !== '/contact-us' && current !== '/delete-account') {
          this.router.navigateByUrl('/login');
        }

      }
    });
  }

  private stopWatching(): void {
    this.userIdle.stopWatching();
  }

  private restartIdleTimer(): void {
    this.userIdle.resetTimer();
  }

  private clearSubscription(): void {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
  }
  ngOnDestroy() {
    this.swUpdateSubscription?.unsubscribe();
    this.displacedSubscription?.unsubscribe();
    this.idleTimerStartSubscription.unsubscribe();
    this.idleTimerStopSubscription.unsubscribe();
    this.pingSubscription.unsubscribe();
  }
}
